import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { slideAction } from '../../store/slideSlice';
import { setManualOfflineMode } from '../../utils/helper';

/**
 * Shared offline-toggle behaviour.
 *
 * Keeps the localStorage-backed manual offline flag and its redux mirror in
 * sync, and tracks the browser's real connectivity so consumers can show the
 * *effective* offline state.
 */
export function useOfflineToggle() {
  const dispatch = useDispatch();
  const isManualOffline = useSelector((state) => state.slide.isManualOffline);
  const [isBrowserOnline, setIsBrowserOnline] = useState(window.navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsBrowserOnline(true);
    const handleOffline = () => setIsBrowserOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const offline = isManualOffline || !isBrowserOnline;

  const toggle = () => {
    // No network means we can't go back online — leave the manual intent alone.
    if (!isBrowserOnline) return;
    const next = !isManualOffline;
    setManualOfflineMode(next);
    dispatch(slideAction.setManualOffline(next));
    // Let listeners react the same way whichever route connectivity returned by.
    if (!next && window.navigator.onLine) {
      window.dispatchEvent(new Event('online'));
    }
  };

  return { offline, isBrowserOnline, isManualOffline, toggle };
}

export default useOfflineToggle;
